import {
    Directive,
    EventEmitter,
    HostListener,
    inject,
    Input,
    input,
    OnDestroy,
    output,
    Output,
    signal,
    ViewContainerRef,
} from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CePopoverOverlayComponent } from './ce-popover-overlay.component';
import { CeOverlayContainerService } from '../ce-overlay/ce-overlay-container.service';
import { CePopoverActiveService } from './ce-popover-active.service';
import { CePopoverActiveTrigger } from './ce-popover-active.types';

export type CePopoverPosition =
    | 'above'
    | 'above-left'
    | 'above-right'
    | 'below'
    | 'below-left'
    | 'below-right'
    | 'left'
    | 'left-top'
    | 'left-bottom'
    | 'right'
    | 'right-top'
    | 'right-bottom'
    | undefined;
export type CePopoverCustomOverlay = { enabled: boolean; overlaySelector: string };

@Directive({
    selector: `[ce-popover-trigger-for]`,
    exportAs: 'ceMenuTrigger',
    standalone: true,
})
export class CePopoverTriggerForDirective implements OnDestroy, CePopoverActiveTrigger {
    content!: CePopoverOverlayComponent;
    #position: CePopoverPosition = undefined;
    #useDefaultWidth = false;
    #overlayRef!: OverlayRef;
    #scrollListeners: { element: Element; listener: () => void }[] = [];
    readonly #overlay = inject(Overlay);
    readonly #popoverContainerService = inject(CeOverlayContainerService);
    readonly #viewContainerRef = inject(ViewContainerRef);
    readonly #popoverActiveService = inject(CePopoverActiveService);
    #disableClick = false;
    #popoverOverlayContainer!: HTMLDivElement | null;
    popoverVisible = false;

    cePopoverContainer = input<string>('');

    worksOnHover = signal(false);

    @Input('ce-popover-trigger-for')
    get _cePopoverTriggerFor(): CePopoverOverlayComponent {
        return this.content;
    }

    set _cePopoverTriggerFor(v: CePopoverOverlayComponent) {
        this.content = v;
    }

    @Input('ce-popover-position')
    get position(): CePopoverPosition {
        return this.#position;
    }

    set position(value: CePopoverPosition) {
        this.#position = value;
    }

    @Input('ce-popover-use-default-width')
    get useDefaultWidth(): boolean {
        return this.#useDefaultWidth;
    }

    set useDefaultWidth(value: boolean) {
        this.#useDefaultWidth = value;
    }

    @Input('ce-popover-disable-click')
    get disableClick(): boolean {
        return this.#disableClick;
    }

    set disableClick(value: boolean) {
        this.#disableClick = value;
    }

    @Input('cePopoverWorksOnHover')
    get _worksOnHover(): boolean {
        return this.worksOnHover();
    }

    set _worksOnHover(value: boolean) {
        this.worksOnHover.set(value);
    }

    /**
     * Defines a special use case: used inside <dialog> or in full screen sized component. Affects the appearance place in DOM.
     */
    cePopoverCustomOverlay = input<CePopoverCustomOverlay | undefined>(undefined);

    @Output()
    popoverAttachedEvent = new EventEmitter<boolean>();

    popoverOpened = output<CePopoverTriggerForDirective>();
    popoverClosed = output<CePopoverTriggerForDirective>();

    #updateOverlayStatus() {
        const isAttached = this.#overlayRef?.hasAttached() || false;
        this.popoverAttachedEvent.emit(isAttached);
    }

    #createOverlayRef() {
        if (this.cePopoverContainer()) {
            this.#popoverOverlayContainer = this.#popoverContainerService.createOverlayContainerWithinContainer({
                className: 'popover-overlay-container',
                container: this.cePopoverContainer() as string,
            });
        } else {
            this.#popoverOverlayContainer =
                this.#popoverContainerService.createOverlayContainer('popover-overlay-container');
        }

        const positionStrategy = this.#overlay
            .position()
            .flexibleConnectedTo(this.#viewContainerRef.element)
            .withGrowAfterOpen(true)
            .withFlexibleDimensions(false)
            .withPush(false)
            .withPositions(this.#getPositionStrategy());

        this.#overlayRef = this.#overlay.create({
            positionStrategy,
            scrollStrategy: this.#overlay.scrollStrategies.reposition(),
            hasBackdrop: false,
            panelClass: `ce-overlay-popover`,
            disposeOnNavigation: true,
        });

        if (!this.cePopoverCustomOverlay()?.enabled) {
            this.#popoverOverlayContainer.appendChild(this.#overlayRef.hostElement);
        } else {
            // If popover is used inside <dialog> or with a full screen mode, it needs be attached to a custom overlay.
            // If it were to appear normally, it wouldn't be visible.
            document
                .querySelector(`${this.cePopoverCustomOverlay()?.overlaySelector}`)
                ?.appendChild(this.#overlayRef.hostElement);
        }
    }

    ngOnDestroy(): void {
        this.#overlayRef?.dispose();
        this.#cleanupListeners();
    }

    @HostListener('click', ['$event'])
    onClick(event: MouseEvent) {
        if (!this.#disableClick) {
            event.stopPropagation();
            // Create the TemplateRef using the ViewChild
            this.#overlayRef?.hasAttached() ? this.hidePopup() : this.showPopup();
        }
    }

    @HostListener('mouseenter', ['$event'])
    onMouseEnter() {
        if (this.worksOnHover()) {
            this.showPopup();
        }
    }

    @HostListener('mouseleave', ['$event'])
    onMouseLeave() {
        if (this.worksOnHover()) {
            this.hidePopup();
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.#overlayRef?.hasAttached()) {
            return;
        }
        let targetElement = event.target as HTMLElement;
        // Traverse up the DOM tree
        while (targetElement !== null) {
            // Check if the current element is a ce-popover element
            if (targetElement.tagName.toLowerCase() === 'ce-popover') {
                // Click event is inside a ce-popover element
                return;
            }
            targetElement = targetElement.parentElement as HTMLElement;
        }
        // Click event is outside a ce-popover element
        if (!this.disableClick) {
            this.hidePopup();
        }
    }

    getPopoverTargetElement() {
        return this.#overlayRef;
    }

    showPopup() {
        if (this.#overlayRef?.hasAttached()) {
            this.hidePopup();
        }

        this.#popoverActiveService.register(this);

        this.#createOverlayRef();

        const content = new TemplatePortal(this.content.templateRef, this.#viewContainerRef);

        this.#overlayRef.attach(content);

        // Set the width of the popover to match the width of the reference element when useDefaultWidth is set to true
        if (this.#useDefaultWidth) {
            // Use requestAnimationFrame to ensure the DOM is updated (needed for iphone on ios)
            requestAnimationFrame(() => {
                const popover = this.#overlayRef.overlayElement.firstChild as HTMLElement;
                const refElement = this.#viewContainerRef.element.nativeElement;
                const refElementWidth = refElement.getBoundingClientRect().width;

                if (popover && refElementWidth > 0) {
                    popover.style.width = `${refElementWidth}px`;
                }
            });
        }
        this.#updateOverlayStatus();
        this.popoverVisible = true;
        this.#attachScrollListenerToElements();
        this.popoverOpened.emit(this);
    }

    hidePopup() {
        this.#popoverActiveService.unregister(this);
        this.#popoverContainerService.removeOverlayContainer(this.#popoverOverlayContainer);
        this.#overlayRef?.detach();
        this.#updateOverlayStatus();
        this.popoverVisible = false;
        this.#cleanupListeners();
        this.popoverClosed.emit(this);
    }

    #resizeObserver: ResizeObserver | null = null;

    #attachScrollListenerToElements() {
        // Select all the relevant elements to attach listeners
        const elements = document.querySelectorAll(
            '.cdk-overlay-container, .CeWizardPanel, .CeModalContent-inner, .TwoColLayout-right'
        );

        // Ensure elements are selected
        if (elements.length === 0) {
            console.warn('No elements found to attach listeners.');
            return; // Exit if no elements are found
        }

        // Function to handle scroll events
        const scrollListener = () => {
            if (this.#overlayRef && this.#overlayRef.hasAttached()) {
                this.#overlayRef.updatePosition();
            }
        };

        // Add a scroll event listener to each element
        elements.forEach((element) => {
            // Apply listener once only
            const existingListener = this.#scrollListeners.find((item) => item.element === element);

            if (!existingListener) {
                element.addEventListener('scroll', scrollListener);
                this.#scrollListeners.push({ element, listener: scrollListener });
            }
        });

        // update position on screen resize
        if (this.#resizeObserver === null) {
            this.#resizeObserver = new ResizeObserver(() => {
                if (this.#overlayRef && this.#overlayRef.hasAttached()) {
                    this.#overlayRef.updatePosition();
                }
            });
        }
        // Observe each element if size changes
        elements.forEach((element) => {
            if (this.#resizeObserver !== null) {
                this.#resizeObserver.observe(element);
            }
        });
    }

    // Method to clean up listeners and observers
    #cleanupListeners() {
        this.#scrollListeners.forEach(({ element, listener }) => {
            element.removeEventListener('scroll', listener);
        });
        this.#scrollListeners = [];

        if (this.#resizeObserver) {
            this.#resizeObserver.disconnect();
            this.#resizeObserver = null;
        }
    }

    #getPositionStrategy(): ConnectedPosition[] {
        // Default position strategy with multiple fallback positions
        if (this.#position === undefined) {
            return [
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                    offsetY: 8,
                }, // Position below target
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'bottom',
                    offsetY: -8,
                }, // Position above target
                {
                    originX: 'end',
                    originY: 'bottom',
                    overlayX: 'end',
                    overlayY: 'top',
                    offsetY: 8,
                }, // Position right of target
                {
                    originX: 'end',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'bottom',
                    offsetY: -8,
                }, // Position left of target
            ];
        }

        // Position strategies for all supported positions
        let positionStrategy: ConnectedPosition[];
        switch (this.position) {
            case 'above':
                positionStrategy = [
                    {
                        originX: 'center',
                        originY: 'top',
                        overlayX: 'center',
                        overlayY: 'bottom',
                        offsetY: -8,
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                        offsetY: -8,
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                        offsetY: -8,
                    },
                ];
                break;
            case 'above-left':
                positionStrategy = [
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                        offsetY: -8,
                    },
                ];
                break;
            case 'above-right':
                positionStrategy = [
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                        offsetY: -8,
                    },
                ];
                break;
            case 'below':
                positionStrategy = [
                    {
                        originX: 'center',
                        originY: 'bottom',
                        overlayX: 'center',
                        overlayY: 'top',
                        offsetY: 8,
                    },
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                        offsetY: 8,
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                        offsetY: 8,
                    },
                ];
                break;
            case 'below-left':
                positionStrategy = [
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                        offsetY: 8,
                    },
                ];
                break;
            case 'below-right':
                positionStrategy = [
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                        offsetY: 8,
                    },
                ];
                break;
            case 'left':
                positionStrategy = [
                    {
                        originX: 'start',
                        originY: 'center',
                        overlayX: 'end',
                        overlayY: 'center',
                        offsetX: -8,
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'top',
                        offsetX: -8,
                    },
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'bottom',
                        offsetX: -8,
                    },
                ];
                break;
            case 'left-top':
                positionStrategy = [
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'top',
                        offsetX: -8,
                    },
                ];
                break;
            case 'left-bottom':
                positionStrategy = [
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'bottom',
                        offsetX: -8,
                    },
                ];
                break;
            case 'right':
                positionStrategy = [
                    {
                        originX: 'end',
                        originY: 'center',
                        overlayX: 'start',
                        overlayY: 'center',
                        offsetX: 8,
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'top',
                        offsetX: 8,
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'bottom',
                        offsetX: 8,
                    },
                ];
                break;
            case 'right-top':
                positionStrategy = [
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'top',
                        offsetX: 8,
                    },
                ];
                break;
            case 'right-bottom':
                positionStrategy = [
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'bottom',
                        offsetX: 8,
                    },
                ];
                break;
            default:
                positionStrategy = [
                    {
                        originX: 'center',
                        originY: 'top',
                        overlayX: 'center',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'center',
                        originY: 'bottom',
                        overlayX: 'center',
                        overlayY: 'top',
                    },
                ];
                break;
        }
        return positionStrategy;
    }
}
